import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Typography, Paper, Button, TextField, Stack, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Alert, InputAdornment, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  UploadFile as UploadIcon,
  Download as DownloadIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// Accepts common header spellings so admins don't have to match an exact template.
const NAME_HEADER_RE  = /^(nom|name|nom\s*complet|full\s*name|professeur|professor)$/i;
const EMAIL_HEADER_RE = /^(e-?mail|adresse\s*e-?mail|courriel)$/i;
const EMAIL_VALID_RE  = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ProfessorManagement = () => {
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialog, setDialog] = useState({ open: false, editing: null, name: '', email: '', saving: false, error: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, professor: null, deleting: false });
  const [importPreview, setImportPreview] = useState(null); // { rows, invalidEmails }
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getProfessors();
      setProfessors(Array.isArray(data) ? data : []);
    } catch {
      setError('Erreur lors du chargement des professeurs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(t);
  }, [success]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return professors;
    return professors.filter(p =>
      p.name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q)
    );
  }, [professors, search]);

  // ── Add / Edit dialog ──────────────────────────────────────────────────
  const openAddDialog = () => setDialog({ open: true, editing: null, name: '', email: '', saving: false, error: '' });
  const openEditDialog = (p) => setDialog({ open: true, editing: p, name: p.name, email: p.email || '', saving: false, error: '' });
  const closeDialog = () => setDialog(d => ({ ...d, open: false }));

  const handleSave = async () => {
    const name = dialog.name.trim();
    const email = dialog.email.trim();
    if (!name) { setDialog(d => ({ ...d, error: 'Le nom complet est obligatoire.' })); return; }
    if (email && !EMAIL_VALID_RE.test(email)) { setDialog(d => ({ ...d, error: 'Adresse e-mail invalide.' })); return; }
    setDialog(d => ({ ...d, saving: true, error: '' }));
    try {
      if (dialog.editing) {
        await adminAPI.updateProfessor(dialog.editing.id, name, email);
        setSuccess(`"${name}" mis à jour.`);
      } else {
        await adminAPI.addProfessor(name, email);
        setSuccess(`"${name}" ajouté.`);
      }
      closeDialog();
      await load();
    } catch (e) {
      setDialog(d => ({ ...d, saving: false, error: e.response?.data?.error || 'Erreur lors de l\'enregistrement.' }));
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteDialog.professor) return;
    setDeleteDialog(d => ({ ...d, deleting: true }));
    try {
      await adminAPI.deleteProfessor(deleteDialog.professor.id);
      setSuccess(`"${deleteDialog.professor.name}" supprimé.`);
      setDeleteDialog({ open: false, professor: null, deleting: false });
      await load();
    } catch {
      setError('Erreur lors de la suppression.');
      setDeleteDialog(d => ({ ...d, deleting: false }));
    }
  };

  // ── Excel template ──────────────────────────────────────────────────────
  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['Nom complet', 'Email'],
      ['pr. Exemple Nom', 'exemple.nom@fsjp.ac.ma'],
    ]);
    ws['!cols'] = [{ wch: 30 }, { wch: 32 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Professeurs');
    XLSX.writeFile(wb, 'modele_import_professeurs.xlsx');
  };

  // ── Excel import ──────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        if (!raw.length) { setError('Le fichier est vide.'); return; }

        // Detect header row: does the first row look like column labels rather
        // than data? If so, find name/email columns by header text; otherwise
        // assume column A = name, column B = email with no header row at all.
        const firstRow = raw[0].map(v => String(v).trim());
        let nameCol = 0, emailCol = 1, dataRows = raw;
        const looksLikeHeader = firstRow.some(v => NAME_HEADER_RE.test(v) || EMAIL_HEADER_RE.test(v));
        if (looksLikeHeader) {
          const foundName = firstRow.findIndex(v => NAME_HEADER_RE.test(v));
          const foundEmail = firstRow.findIndex(v => EMAIL_HEADER_RE.test(v));
          nameCol = foundName >= 0 ? foundName : 0;
          emailCol = foundEmail >= 0 ? foundEmail : 1;
          dataRows = raw.slice(1);
        }

        const rows = [];
        const invalidEmails = [];
        dataRows.forEach((r) => {
          const name = String(r[nameCol] ?? '').trim();
          const email = String(r[emailCol] ?? '').trim();
          if (!name) return; // skip blank lines
          if (email && !EMAIL_VALID_RE.test(email)) invalidEmails.push(name);
          rows.push({ name, email: email || null });
        });

        if (!rows.length) { setError('Aucune ligne exploitable trouvée dans le fichier.'); return; }
        setImportPreview({ rows, invalidEmails, fileName: file.name });
      } catch (err) {
        setError('Erreur de lecture du fichier Excel : ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const result = await adminAPI.bulkImportProfessors(importPreview.rows);
      setSuccess(`Import terminé : ${result.inserted} ajouté(s), ${result.updated} mis à jour${result.skipped?.length ? `, ${result.skipped.length} ignoré(s)` : ''}.`);
      setImportPreview(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'import.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SchoolIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" color="primary" fontWeight={700}>Professeurs</Typography>
            <Typography variant="body2" color="text.secondary">
              Annuaire des enseignants — utilisé pour remplir les champs professeur dans
              la Programmation des Cours et la Planification des Examens.
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <input type="file" accept=".xlsx,.xls" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate} sx={{ borderRadius: 2 }}>
            Modèle Excel
          </Button>
          <Button variant="outlined" color="secondary" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()} sx={{ borderRadius: 2 }}>
            Importer Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} sx={{ borderRadius: 2 }}>
            Ajouter
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Search + count */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Rechercher par nom ou email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 300 }}
        />
        <Chip label={`${filtered.length} professeur(s)`} color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1.5px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled' }}>
            <SchoolIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography>
              {professors.length === 0
                ? "Aucun professeur enregistré — importez une liste Excel ou ajoutez-en un manuellement."
                : "Aucun résultat pour cette recherche."}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Nom complet</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} sx={{ '&:hover': { bgcolor: '#f0f4ff' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                    <TableCell>
                      {p.email ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'text.secondary' }}>
                          <EmailIcon sx={{ fontSize: 15 }} />
                          <Typography variant="body2">{p.email}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">Non renseigné</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => openEditDialog(p)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, professor: p, deleting: false })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {dialog.editing ? 'Modifier le professeur' : 'Ajouter un professeur'}
          <IconButton size="small" onClick={closeDialog}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {dialog.error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{dialog.error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nom complet" fullWidth autoFocus required
              value={dialog.name}
              onChange={(e) => setDialog(d => ({ ...d, name: e.target.value }))}
            />
            <TextField
              label="Email" type="email" fullWidth
              value={dialog.email}
              onChange={(e) => setDialog(d => ({ ...d, email: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={dialog.saving}>
            {dialog.saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, professor: null, deleting: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer ce professeur ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{deleteDialog.professor?.name}</strong> sera retiré de l'annuaire. Cela n'affecte pas les
            séances/examens déjà enregistrés à son nom.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, professor: null, deleting: false })}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteDialog.deleting}>
            {deleteDialog.deleting ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import preview/confirm */}
      <Dialog open={!!importPreview} onClose={() => !importing && setImportPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmer l'import</DialogTitle>
        <DialogContent>
          {importPreview && (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Fichier <strong>{importPreview.fileName}</strong> — <strong>{importPreview.rows.length}</strong> professeur(s) détecté(s).
                Un professeur déjà présent (même nom) sera mis à jour, pas dupliqué.
              </Typography>
              {importPreview.invalidEmails.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
                  {importPreview.invalidEmails.length} ligne(s) ont un email qui ne semble pas valide et seront
                  importées sans email : {importPreview.invalidEmails.slice(0, 5).join(', ')}
                  {importPreview.invalidEmails.length > 5 ? '…' : ''}
                </Alert>
              )}
              <TableContainer sx={{ maxHeight: 280, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.rows.slice(0, 100).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.email || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {importPreview.rows.length > 100 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Aperçu limité aux 100 premières lignes — les {importPreview.rows.length - 100} suivantes seront importées aussi.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportPreview(null)} disabled={importing}>Annuler</Button>
          <Button variant="contained" onClick={confirmImport} disabled={importing}>
            {importing ? 'Import en cours...' : `Importer ${importPreview?.rows.length || ''} professeur(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfessorManagement;
